import { lazy, Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate, Link, useNavigate } from 'react-router-dom';
import { getUser, redirectToLogin, type RwUser } from './lib/auth.js';
import { PageView } from './pages/PageView.js';
import { Home } from './pages/Home.js';

// Sandbox route — local-only UX playground. Not authenticated, mock API, never
// touches the live backend. Lazy-loaded so its bundle stays out of the live read path.
const SandboxApp = lazy(() =>
  import('./sandbox/SandboxApp.js').then((m) => ({ default: m.SandboxApp })),
);

export function App() {
  return (
    <Routes>
      <Route
        path="/sandbox/*"
        element={
          <Suspense fallback={<div className="p-12 text-brand-500">Loading sandbox…</div>}>
            <SandboxApp />
          </Suspense>
        }
      />
      <Route path="*" element={<AuthedApp />} />
    </Routes>
  );
}

function AuthedApp() {
  // Cheap synchronous auth gate. The token is in a cookie, so we can verify before render.
  const [user, setUser] = useState<RwUser | null>(() => getUser());

  useEffect(() => {
    if (!user) {
      redirectToLogin();
      return;
    }
    // Re-check on window focus in case the token expired in another tab
    const onFocus = () => {
      const fresh = getUser();
      if (!fresh) redirectToLogin();
      else setUser(fresh);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [user]);

  if (!user) return null;

  return (
    <div className="flex flex-col h-full w-full bg-white">
      <InternalLinkInterceptor />
      <header className="border-b border-brand-100 px-12 py-3 flex items-center bg-white shrink-0">
        <Link
          to="/"
          className="font-display font-bold text-brand-700 text-lg leading-none hover:text-brand-800"
        >
          Redwood Wiki
        </Link>
      </header>
      <main className="flex-1 overflow-y-auto">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/p/:id" element={<PageView />} />
          {/* /p/:id/edit was the legacy edit route. Editing is now inline at /p/:id. */}
          <Route path="/p/:id/edit" element={<Navigate to=".." replace relative="path" />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

// Document-level click interceptor for `<a data-internal-link>` anchors emitted
// by editor blocks (e.g. the pageLink inline content). Lives here, above any
// editor remount, so the captured navigate() doesn't subscribe blocks to
// router context — which would crash BlockNote NodeViews on navigation.
// See client/src/components/editor/PageLinkBlock.tsx for the why.
function InternalLinkInterceptor() {
  const navigate = useNavigate();
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented) return;
      if (e.button !== 0) return;
      // Honor modifier-clicks (open in new tab etc.) — let the browser handle them.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as HTMLElement | null)?.closest?.(
        'a[data-internal-link="true"]',
      ) as HTMLAnchorElement | null;
      if (!a) return;
      const href = a.getAttribute('href');
      if (!href || !href.startsWith('/')) return;
      e.preventDefault();
      navigate(href);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [navigate]);
  return null;
}
